import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Brain,
  ChevronRight,
  Loader2,
  MessageSquare,
  Sparkles,
  FileText,
  Lightbulb,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { usePersonasStore } from '@/stores/personas';
import { agentLabApi, ApiError, type PersonaClient, type PersonaDetail } from '@/services/api';

type ChatRole = 'user' | 'assistant' | 'system';

interface ChatMessage {
  id: string;
  role: ChatRole;
  title: string;
  summary: string;
  detailJson?: unknown;
  href?: string;
}

function clientSlugForUrl(c: PersonaClient): string {
  return c.client_uid ?? c.slug ?? c.client_id;
}

function formatClientName(client: PersonaClient): string {
  if (client.client_name) return client.client_name;
  const slug = client.slug ?? client.client_uid ?? client.client_id;
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const GUIDES: { id: string; title: string; body: string }[] = [
  {
    id: 'flow-ideas',
    title: 'Creative brief ideas',
    body: 'Runs the same path as the suggest_briefs MCP tool: ranked ideas, resonance note, chain_suggestion into draft.',
  },
  {
    id: 'flow-draft',
    title: 'Draft brief + angles',
    body: 'Matches draft_brief: three sections, PDO-enriched angles, promote_to_brief payload for the full brief step.',
  },
  {
    id: 'flow-pdo',
    title: 'PDO context (ontology)',
    body: 'Loads concept activations and causal edges from the in-memory Purchase Decision Ontology — no graph DB required.',
  },
  {
    id: 'flow-ask',
    title: 'Ask the persona',
    body: 'Uses POST /api/v1/persona/query (ontology engine). Good for validating “ask_persona”-style questions before MCP clients.',
  },
];

export function AgentLabPage() {
  const {
    clients,
    selectedClientId,
    personas,
    loading: storeLoading,
    error: storeError,
    fetchClients,
    selectClient,
  } = usePersonasStore();

  const [personaSlug, setPersonaSlug] = useState('');
  const [premise, setPremise] = useState('');
  const [question, setQuestion] = useState('What concerns stop this persona from trying a new snack brand?');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.client_id === selectedClientId) ?? null,
    [clients, selectedClientId],
  );

  const clientUid = selectedClient ? clientSlugForUrl(selectedClient) : '';

  useEffect(() => {
    if (!personas.length) {
      setPersonaSlug('');
      return;
    }
    setPersonaSlug((prev) => {
      if (prev && personas.some((p) => (p.slug ?? p.id) === prev)) return prev;
      return personas[0].slug ?? personas[0].id ?? '';
    });
  }, [personas]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  const pushAssistant = (title: string, summary: string, detailJson?: unknown, href?: string) => {
    setMessages((m) => [
      ...m,
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        title,
        summary,
        detailJson,
        href,
      },
    ]);
  };

  const pushUser = (title: string) => {
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: 'user', title, summary: '' }]);
  };

  const handleError = (err: unknown, label: string) => {
    const msg =
      err instanceof ApiError ? `${err.status}: ${err.body.slice(0, 400)}` : String(err);
    pushAssistant(label, msg);
  };

  const runBriefIdeas = async () => {
    if (!clientUid || !personaSlug) return;
    setBusy(true);
    pushUser(`Suggest brief ideas · ${clientUid} / ${personaSlug}`);
    try {
      const data = await agentLabApi.invokeBriefIdeas({
        client_uid: clientUid,
        persona_id: personaSlug,
        max_ideas: 4,
      });
      const ideas = (data.ideas as Array<{ title?: string; predicted_resonance?: number }>) ?? [];
      const top = ideas[0];
      pushAssistant(
        'Brief ideas',
        `${ideas.length} ideas returned${top?.title ? `. Top: “${top.title}” (${Number(top.predicted_resonance ?? 0).toFixed(1)} resonance).` : '.'}`,
        data,
        `/personas/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaSlug)}`,
      );
    } catch (e) {
      handleError(e, 'Brief ideas failed');
    } finally {
      setBusy(false);
    }
  };

  const runBriefDraft = async () => {
    if (!clientUid || !personaSlug) return;
    setBusy(true);
    pushUser(`Draft brief · ${premise ? 'custom premise' : 'default premise'}`);
    try {
      const data = await agentLabApi.invokeBriefDraft({
        client_uid: clientUid,
        persona_id: personaSlug,
        premise: premise.trim() || undefined,
        num_angles: 3,
      });
      const sections = data.sections as Record<string, string> | undefined;
      const angles = (data.angles as unknown[])?.length ?? 0;
      pushAssistant(
        'Draft brief',
        `Sections: ${sections ? Object.keys(sections).join(', ') : '—'}. Angles: ${angles}.`,
        data,
        `/personas/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaSlug)}`,
      );
    } catch (e) {
      handleError(e, 'Draft brief failed');
    } finally {
      setBusy(false);
    }
  };

  const runOntologyContext = async () => {
    if (!clientUid || !personaSlug) return;
    setBusy(true);
    pushUser(`PDO ontology context · ${clientUid} / ${personaSlug}`);
    try {
      const data = await agentLabApi.getOntologyContext(clientUid, personaSlug);
      const n = Number(data.total_activated_concepts ?? 0);
      const edges = Array.isArray(data.causal_edges) ? data.causal_edges.length : 0;
      pushAssistant(
        'Ontology context',
        data.available
          ? `${n} activated concepts, ${edges} causal edges (in-memory PDO).`
          : `Unavailable: ${String(data.reason ?? 'unknown')}`,
        data,
        `/ontology`,
      );
    } catch (e) {
      handleError(e, 'Ontology context failed');
    } finally {
      setBusy(false);
    }
  };

  const runPersonaQuery = async () => {
    if (!clientUid || !personaSlug || !question.trim()) return;
    setBusy(true);
    pushUser(`Persona query · “${question.slice(0, 80)}${question.length > 80 ? '…' : ''}”`);
    try {
      const data = await agentLabApi.personaQuery({
        client_uid: clientUid,
        persona_slug: personaSlug,
        question: question.trim(),
        include_evidence: true,
      });
      const ans = data.answer as { summary?: string } | undefined;
      pushAssistant(
        'Persona answer',
        ans?.summary?.slice(0, 500) ?? 'See JSON for structured answer.',
        data,
        `/personas/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaSlug)}`,
      );
    } catch (e) {
      handleError(e, 'Persona query failed');
    } finally {
      setBusy(false);
    }
  };

  const clearThread = () => setMessages([]);

  const personaLink =
    clientUid && personaSlug
      ? `/personas/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaSlug)}`
      : null;

  return (
    <div className="space-y-6 pb-16">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-brand-teal mb-1">
            <Bot size={18} />
            <span className="text-[10px] font-mono uppercase tracking-widest">Internal QA</span>
          </div>
          <h1 className="text-2xl font-bold text-navy-900">Agent Lab</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Exercise MCP-equivalent flows against the Core API (same payloads as{' '}
            <code className="text-xs bg-slate-100 px-1 rounded">suggest_briefs</code>,{' '}
            <code className="text-xs bg-slate-100 px-1 rounded">draft_brief</code>, and persona query)
            without a Claude Desktop connection. Use this to QA brief pipelines and PDO grounding before
            shipping MCP changes.
          </p>
        </div>
        {personaLink && (
          <Link
            to={personaLink}
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:underline"
          >
            Open persona detail (cognitive layer)
            <ChevronRight size={16} />
          </Link>
        )}
      </div>

      {/* Guided flows */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {GUIDES.map((g) => (
          <div
            key={g.id}
            className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-600"
          >
            <div className="font-semibold text-navy-900 text-xs mb-1">{g.title}</div>
            <p className="text-xs leading-relaxed">{g.body}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Session</span>
          {storeLoading && <Loader2 size={14} className="animate-spin text-slate-400" />}
          {storeError && <span className="text-xs text-red-600">{storeError}</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block text-xs font-medium text-slate-600">
            Client
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={selectedClientId ?? ''}
              onChange={(e) => selectClient(e.target.value)}
            >
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {formatClientName(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-medium text-slate-600">
            Persona
            <select
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={personaSlug}
              onChange={(e) => setPersonaSlug(e.target.value)}
            >
              {personas.map((p: PersonaDetail) => (
                <option key={p.id} value={p.slug ?? p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-xs font-medium text-slate-600">
          Optional premise (draft brief)
          <textarea
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[72px]"
            placeholder="e.g. Back-to-school lunchbox decisions with allergen-safe proof…"
            value={premise}
            onChange={(e) => setPremise(e.target.value)}
          />
        </label>

        <label className="block text-xs font-medium text-slate-600">
          Persona question (ontology query)
          <textarea
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[72px]"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </label>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy || !clientUid || !personaSlug}
            onClick={runBriefIdeas}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white',
              busy || !clientUid || !personaSlug ? 'bg-slate-300 cursor-not-allowed' : 'bg-brand-teal hover:bg-brand-teal/90',
            )}
          >
            <Lightbulb size={16} />
            Suggest brief ideas
          </button>
          <button
            type="button"
            disabled={busy || !clientUid || !personaSlug}
            onClick={runBriefDraft}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white',
              busy || !clientUid || !personaSlug ? 'bg-slate-300 cursor-not-allowed' : 'bg-navy-900 hover:bg-navy-800',
            )}
          >
            <FileText size={16} />
            Draft brief
          </button>
          <button
            type="button"
            disabled={busy || !clientUid || !personaSlug}
            onClick={runOntologyContext}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold border',
              busy || !clientUid || !personaSlug
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-brand-teal text-brand-teal hover:bg-brand-teal/5',
            )}
          >
            <Brain size={16} />
            Load PDO context
          </button>
          <button
            type="button"
            disabled={busy || !clientUid || !personaSlug || !question.trim()}
            onClick={runPersonaQuery}
            className={clsx(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold border',
              busy || !clientUid || !personaSlug || !question.trim()
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50',
            )}
          >
            <MessageSquare size={16} />
            Ask persona
          </button>
          <button
            type="button"
            disabled={busy || messages.length === 0}
            onClick={clearThread}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 ml-auto"
          >
            <Trash2 size={16} />
            Clear
          </button>
        </div>
      </div>

      {/* Chat-style thread */}
      <div className="rounded-2xl border border-slate-200 bg-white min-h-[320px] max-h-[calc(100vh-12rem)] overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-16 text-slate-400 text-sm">
            <Sparkles className="mx-auto mb-3 opacity-40" size={32} />
            Choose a client and persona, then run a guided action. Responses appear here with expandable JSON.
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={clsx(
              'rounded-xl px-4 py-3 border text-sm',
              msg.role === 'user'
                ? 'bg-slate-50 border-slate-200 text-slate-700 ml-8'
                : 'bg-gradient-to-br from-brand-teal/5 to-indigo-50/40 border-brand-teal/20 mr-8',
            )}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-bold text-navy-900 uppercase tracking-wide">
                {msg.role === 'user' ? 'You' : msg.title}
              </span>
              {msg.href && (
                <Link to={msg.href} className="text-[10px] text-brand-teal flex items-center gap-0.5 hover:underline">
                  <ExternalLink size={10} />
                  Related UI
                </Link>
              )}
            </div>
            {msg.role === 'user' ? (
              <p className="text-sm">{msg.title}</p>
            ) : (
              <>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{msg.summary}</p>
                {msg.detailJson != null && (
                  <details className="mt-2">
                    <summary className="text-xs cursor-pointer text-brand-teal font-medium">Raw JSON</summary>
                    <pre className="mt-2 text-[10px] overflow-x-auto bg-navy-900 text-brand-teal/90 rounded-lg p-3 max-h-64 overflow-y-auto">
                      {JSON.stringify(msg.detailJson, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            )}
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
            <Loader2 size={16} className="animate-spin" />
            Calling Core API…
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
